import { Container, Sprite, type Filter } from "pixi.js";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import { resolveSymbolView } from "../../view/grid/SymbolView";
import { ShineSweepFilter } from "../../filters/ShineSweepFilter";
import type { SymbolWinEffect, SymbolWinAnimationRequest } from "./SymbolWinEffect";
import type { ShineSweepTuning } from "./ShineSweepTuning";

interface ActiveShine {
  /** The sprite's filters before the swap — restored on release. */
  originalFilters: Filter[] | null;
  filter: ShineSweepFilter;
  gen: number;
}

/**
 * Pluggable `SymbolWinEffect` that plays a **one-shot shine-sweep** on each winning symbol — a
 * tilted specular band travels across the sprite once, via a per-sprite `ShineSweepFilter`
 * (`rgb *= 1 + highlight·band`, hue-preserving, respects the sprite's alpha shape). Stacks with the
 * scale-pulse (it only touches the sprite's filters). `progress` ramps 0 → 1 on engine time.
 *
 * `enabled: false` makes every call a no-op. `{ once: true }` sweeps each sprite once per session.
 */
export class ShineSweepSymbolWinEffect implements SymbolWinEffect {
  private readonly enabled: boolean;
  private readonly tuning: ShineSweepTuning;
  private readonly taskRunner: PresentationTaskRunner;
  private readonly once: boolean;
  private readonly active: Map<Sprite, ActiveShine> = new Map();
  private readonly shined: Set<Sprite> = new Set();
  private generation = 0;

  public constructor(input: {
    /** Set false to disable the effect entirely. */
    enabled?: boolean;
    tuning: ShineSweepTuning;
    taskRunner: PresentationTaskRunner;
    once?: boolean;
  }) {
    this.enabled = input.enabled ?? true;
    this.tuning = input.tuning;
    this.taskRunner = input.taskRunner;
    this.once = input.once ?? false;
  }

  public play(request: SymbolWinAnimationRequest): void {
    const cell = request.cellNode;
    if (!cell || cell.destroyed) return;
    const sprite = resolveSymbolView(cell)?.symbolSprite ?? null;
    if (sprite) this.sweep(sprite);
  }

  /**
   * Sweep every `Sprite` in an arbitrary container's subtree — the seam for NON-grid consumers
   * (e.g. a glint across the cannon's queue rounds). Prunes destroyed sprites first.
   */
  public sweepNode(node: Container): void {
    if (!this.enabled || !node || node.destroyed) return;
    this.pruneInvalid();
    const visit = (c: Container): void => {
      if (c instanceof Sprite) this.sweep(c);
      for (const child of c.children) visit(child);
    };
    visit(node);
  }

  private pruneInvalid(): void {
    for (const sprite of Array.from(this.active.keys())) {
      if (sprite.destroyed) {
        this.active.delete(sprite);
        this.shined.delete(sprite);
      }
    }
  }

  /**
   * Takes every shine off, `revert: false` included. That flag is the reels' spin-start clear keeping
   * what is on screen (the dim must not snap bright as the reels start), but a band is a passing
   * highlight, not state: until 2026-09-26 it stayed on the pooled sprite at whatever brightness the
   * sweep had reached, into the next spin's symbols (review finding 45).
   */
  public stopAll(_opts?: { revert?: boolean }): void {
    for (const sprite of Array.from(this.active.keys())) this.release(sprite);
    this.shined.clear();
  }

  /** Restore a sprite's original filters + clear its tracking. Idempotent. */
  public release(sprite: Sprite): void {
    const entry = this.active.get(sprite);
    if (!entry) return;
    entry.gen = ++this.generation;
    this.active.delete(sprite);
    this.shined.delete(sprite);
    if (!sprite.destroyed) sprite.filters = entry.originalFilters;
    entry.filter.destroy();
  }

  private sweep(sprite: Sprite): void {
    if (!this.enabled || sprite.destroyed) return;
    if (this.once && this.shined.has(sprite)) return;
    this.shined.add(sprite);

    const entry = this.ensureInstance(sprite);
    const gen = ++this.generation;
    entry.gen = gen;
    const filter = entry.filter;

    const t = this.tuning;
    const isCurrent = (): boolean => !sprite.destroyed && this.active.get(sprite)?.gen === gen;
    filter.highlight = 0; // band hidden until the ramp actually runs
    void (async () => {
      if (t.startDelaySeconds > 0) {
        await this.taskRunner.sleep(t.startDelaySeconds);
        if (!isCurrent()) return;
      }
      filter.progress = 0;
      filter.highlight = t.peak;
      if (t.sweepSeconds > 0) {
        await this.taskRunner.tweenProgress(
          t.sweepSeconds,
          (t01) => {
            if (isCurrent()) filter.progress = t01;
          },
          undefined,
          { easing: t.easing },
        );
        if (!isCurrent()) return;
      }
      filter.progress = 1;
      filter.highlight = 0;
    })();
  }

  private ensureInstance(sprite: Sprite): ActiveShine {
    let entry = this.active.get(sprite);
    if (!entry) {
      const t = this.tuning;
      const filter = new ShineSweepFilter({ highlight: 0, tilt: t.tilt, width: t.width, softness: t.softness, progress: 0, autoSpeed: 0 });
      const original = sprite.filters ? [...sprite.filters] : null;
      entry = { originalFilters: original, filter, gen: 0 };
      sprite.filters = [...(original ?? []), filter];
      this.active.set(sprite, entry);
    }
    return entry;
  }
}
